import { useEffect } from "react"

/**
 * useFocusTrap
 * @param {string} container CSS selector for the container that should trap focus
 * @param {string} [trigger] Optional CSS selector for a close button outside the container that can get focus
 * @param {boolean} [opened] Optional param to trigger autofocus if trigger is not trapped in focus state
 * @example useFocusTrap('#modal', null, isOpened)
 * @example useFocusTrap('#popover', '#anchor')
 */
export function useFocusTrap(container, trigger, opened) {
  useEffect(() => {
    const focusElems =
      'button, a, select, input, textarea, [tabindex]:not([tabindex="-1"])'
    // if no trigger is specified, lock focus within content of container only
    if (!trigger) {
      document
        .querySelector(container)
        ?.querySelectorAll(focusElems)[0]
        .focus()
    }
    document.addEventListener("keydown", e => {
      const containerElement = document.querySelector(container)
      if (!containerElement) return
      const focusableElements = containerElement.querySelectorAll(focusElems)
      const firstFocusable = focusableElements[0]
      const lastFocusable = focusableElements[focusableElements.length - 1]
      if (e.code !== "Tab") return
      if (e.shiftKey) {
        // backwards tab navigation when shift key is pressed
        if (firstFocusable === document.activeElement) {
          if (trigger && document.querySelector(trigger)) {
            document.querySelector(trigger).focus()
          } else {
            lastFocusable.focus()
          }
          e.preventDefault()
        } else if (lastFocusable === document.activeElement) {
          focusableElements[focusableElements.length - 2].focus()
          e.preventDefault()
        }
      } else {
        if (document.querySelector(trigger) === document.activeElement) {
          firstFocusable.focus()
          e.preventDefault()
        } else if (lastFocusable === document.activeElement) {
          if (trigger && document.querySelector(trigger)) {
            document.querySelector(trigger).focus()
          } else {
            firstFocusable.focus()
          }
          e.preventDefault()
        }
      }
    })
  }, [container, trigger, opened])
}
